var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getCrossOriginWorkerURL } from "@auvious/utils";
import { Cursor } from "../events/cursor";
import { Input } from "../events/input";
import { Location } from "../events/location";
import { Observer } from "../events/observer";
import { Resize } from "../events/resize";
import { Scroll } from "../events/scroll";
import { COBROWSER_PERMISSION } from "../../model";
import { HighLighter } from "../events/highlight";
import { Sheet } from "../events/sheet";
import { PageBase } from "./base";
import { Filter } from "./filter";
import { Iframe } from "./iframe";
import { Serializer } from "./serializer";
export class Page extends PageBase {
    constructor(options, users, handlers = {}) {
        var _a;
        super(options.target || document.documentElement, options.user);
        this.users = users;
        this.handlers = handlers;
        this.isOwner = true;
        this.isRoot = true;
        this.canControl = false;
        this.syncTimeout = -1;
        this.options = Object.assign({ parentId: 0, targetId: 0 }, options);
        this.workerPath = options.webworkerPath;
        this.allowedOrigins = ((options === null || options === void 0 ? void 0 : options.allowOrigins) || []).join("\n");
        (_a = this.compressionSupport) !== null && _a !== void 0 ? _a : (this.compressionSupport = {
            zstd: {
                compress: false,
                decompress: false,
            },
            gzip: {
                compress: false,
                decompress: false,
            },
        });
    }
    static create(...args) {
        return __awaiter(this, void 0, void 0, function* () {
            const inst = new Page(...args);
            yield inst.init();
            console.log("Cobrowser: Initialized session at", inst.href);
            return inst;
        });
    }
    onBatch(ev) {
        var _a, _b;
        (_b = (_a = this.handlers).flush) === null || _b === void 0 ? void 0 : _b.call(_a, ev.events ? ev : null);
    }
    onSetEvents(events) {
        super.setEvents(events);
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
            this.documentId = 1;
            if (!this.worker) {
                const url = yield getCrossOriginWorkerURL(this.workerPath);
                this.worker = new Worker(url);
                if (url.startsWith("blob:"))
                    URL.revokeObjectURL(url);
            }
            (_a = this.resize) !== null && _a !== void 0 ? _a : (this.resize = new Resize(this, (ev) => {
                this.messenger.sendPageUpdate(this.event("ResizeUpdate", ev));
            }));
            (_b = this.filter) !== null && _b !== void 0 ? _b : (this.filter = new Filter(this));
            (_c = this.serializer) !== null && _c !== void 0 ? _c : (this.serializer = new Serializer(this));
            (_d = this.input) !== null && _d !== void 0 ? _d : (this.input = new Input(this, (ev) => this.messenger.sendPageUpdate(this.event("InputUpdate", ev))));
            (_e = this.cursor) !== null && _e !== void 0 ? _e : (this.cursor = new Cursor(this, (ev) => this.messenger.sendPageUpdate(this.event("Cursor", ev))));
            (_f = this.scroll) !== null && _f !== void 0 ? _f : (this.scroll = new Scroll(this, (ev) => this.messenger.sendPageUpdate(this.event("ScrollUpdate", ev))));
            (_g = this.location) !== null && _g !== void 0 ? _g : (this.location = new Location(this, (ev) => {
                this.messenger.sendPageUpdate(this.event("Navigation", ev));
            }));
            (_h = this.sheet) !== null && _h !== void 0 ? _h : (this.sheet = new Sheet(this, (ev) => {
                this.messenger.sendPageUpdate(this.event("SheetUpdate", ev));
            }));
            (_j = this.highlighter) !== null && _j !== void 0 ? _j : (this.highlighter = new HighLighter(this, (ev) => this.messenger.sendPageUpdate(this.event("HighLight", ev))));
            this.worker.postMessage({
                type: "InitWorkerOptions",
                endpoint: (_k = this.options.user) === null || _k === void 0 ? void 0 : _k.endpoint,
                compression: this.compressionSupport,
            });
            const workerPort = this.messenger.setupRoot();
            this.worker.postMessage({ type: "DocumentRegistered", id: this.documentId, port: workerPort }, [workerPort]);
            this.messenger.sendPageUpdate(this.event("PageMetadata", {
                type: "UserInfo",
                data: {
                    id: (_l = this.options.user) === null || _l === void 0 ? void 0 : _l.id,
                    endpoint: (_m = this.options.user) === null || _m === void 0 ? void 0 : _m.endpoint,
                    metadata: (_o = this.options.user) === null || _o === void 0 ? void 0 : _o.metadata,
                },
            }));
            this.messenger.sendPageUpdate(this.event("Document", {
                id: this.documentId,
                target: 0,
            }));
            this.track();
        });
    }
    render(ev) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const id = this.users.getId(ev.endpoint);
            switch (ev.type) {
                case "Batch":
                    yield this.handleRemoteBatch(ev);
                    break;
                case "InputUpdate":
                    if (this.users.hasPermission(id, COBROWSER_PERMISSION.CONTROL)) {
                        if (ev.document === this.documentId) {
                            this.handleRemoteInput(ev);
                        }
                        else {
                            this.messenger.sendRender(ev);
                        }
                    }
                    break;
                case "ScrollUpdate":
                    if (ev.document === this.documentId) {
                        this.handleRemoteScroll(ev);
                    }
                    else {
                        this.messenger.sendRender(ev);
                    }
                    break;
                case "Cursor":
                    if (ev.payload.type === "mousemove" ||
                        this.users.hasPermission(id, COBROWSER_PERMISSION.CONTROL)) {
                        if (ev.document === this.documentId) {
                            this.handleRemoteCursor(ev);
                        }
                        else {
                            this.messenger.sendRender(ev);
                        }
                        if (ev.payload.type === "mousemove")
                            (_b = (_a = this.handlers)[ev.payload.type]) === null || _b === void 0 ? void 0 : _b.call(_a, {
                                left: ev.payload.clientX,
                                top: ev.payload.clientY,
                                type: ev.payload.type,
                                user: { id, endpoint: ev.endpoint },
                            });
                    }
                    break;
                case "PageMetadata":
                    if (ev.payload.type === "UserInfo") {
                        this.users.addUser({ id: ev.payload.data.id, endpoint: ev.payload.data.endpoint }, null, ev.payload.data.metadata);
                    }
                    break;
                case "HighLight":
                    yield this.highlighter.dispatch(ev.payload);
                    break;
                // case "SyncRequest":
                //   page.sync();
                //   break;
            }
        });
    }
    handleRemoteBatch(_a) {
        return __awaiter(this, arguments, void 0, function* ({ payload }) {
            for (const e of payload.events || []) {
                yield this.render(e);
            }
        });
    }
    // root needs no positioning
    requestAbsolutePosition() { }
    setEvents(events) {
        this.worker.postMessage({ type: "SetEvents", events });
    }
    sync() {
        if (this.getTime() - this.lastSync > Page.SyncRequestPeriod) {
            this.lastSync = this.getTime();
            this.reset();
        }
        else {
            this.lastSync += Page.SyncRequestPeriod;
            clearTimeout(this.syncTimeout);
            this.syncTimeout = window.setTimeout(() => this.reset(), this.lastSync - this.getTime());
        }
    }
    reset() {
        var _a;
        (_a = this.observer) === null || _a === void 0 ? void 0 : _a.untrack();
        this.cleanIdp();
        this.observer = new Observer(this);
        this.init();
    }
    /** @param lite - merges events to form a page instance */
    flush() {
        return __awaiter(this, arguments, void 0, function* (lite = false) {
            this.messenger.sendFlushRequest();
        });
    }
    createIframe(target, options) {
        return new Iframe(target, options);
    }
    track() {
        var _a, _b;
        (_a = this.resize) === null || _a === void 0 ? void 0 : _a.track();
        (_b = this.location) === null || _b === void 0 ? void 0 : _b.track();
        this.sheet.track();
        this.observer.track(this.target.tagName === "IFRAME"
            ? this.getDocument().documentElement
            : this.target);
        this.animate.track();
    }
    cleanup() {
        var _a, _b, _c;
        (_a = this.observer) === null || _a === void 0 ? void 0 : _a.untrack();
        (_b = this.serializer) === null || _b === void 0 ? void 0 : _b.cleanup();
        this.sheet.untrack();
        super.cleanup();
        (_c = this.worker) === null || _c === void 0 ? void 0 : _c.terminate();
    }
}
//# sourceMappingURL=page.js.map